import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import ClientDropdown from "components/Dropdowns/ClientDropdown";
import InputField from "components/Input/InputField";
import { API_URL, groupedVisaOptions, visaWorkStatus } from "const/constants";
import useKothar from "context/useKothar";
import { differenceInDays } from "date-fns";
import { formatDisplayDate, parseToDate } from "utils/dateFormat";
import { useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getThemedTabsSx } from "theme/tabsTheme";
import EntryDocumentsPanel from "components/ModuleDocuments/EntryDocumentsPanel";
import SignatureDocumentsPanel from "components/Visa/SignatureDocumentsPanel";
import SendNotificationModal from "../Accounts/SendNotificationModal";
import ModulePaymentsTab from "components/ModulePayments/ModulePaymentsTab";
import ModuleTasksTab from "components/Tasks/ModuleTasksTab";
import VisaStatusHistoryTab from "./AddVisa/VisaStatusHistoryTab";
import { useVisaStatusHistory } from "./visaStatusHistoryApi";
import { usePreselectClientFromLocation } from "../shared/moduleClients/usePreselectClientFromLocation";
import ModuleLockedTab from "../shared/ModuleLockedTab";
import { blankIfPlaceholder } from "utils/textNormalize";
import { promptReferralShare } from "../Clients/ReferralSharePrompt";
import { computeFinalAmount, computeTotal, defaultGstSetting } from "utils/gstCalc";
import GstChargeField from "components/GstChargeField";
import ViewClientProfile from "components/Clients/ViewClientProfile";

const TAB_DETAILS = 0;
const TAB_DOCUMENTS = 1;
const TAB_STATUS = 2;
const TAB_PAYMENTS = 3;
const TAB_TASKS = 4;

const LEGACY_DOCUMENT_FIELDS = [
  { key: "passport", name: "Passport" },
  { key: "resume", name: "Resume" },
  { key: "birthCertificate", name: "Birth Certificate" },
  { key: "currentVisa", name: "Current Visa" },
  { key: "insuranceDoc", name: "Insurance" },
  { key: "allAcademic", name: "Academic" },
  { key: "coe", name: "COE" },
  { key: "completionLetter", name: "Completion Letter" },
  { key: "graduationCertificate", name: "Graduation Certificate" },
  { key: "ieltsPte", name: "IELTS/PTE" },
  { key: "policeReport", name: "Police Report" },
  { key: "relationshipCertificate", name: "Relationship Certificate" },
  { key: "marriageCertificate", name: "Marriage Certificate" },
];

const normalizeVisaDocuments = (item = {}) => {
  if (Array.isArray(item.documents) && item.documents.length > 0) {
    return item.documents;
  }
  if (Array.isArray(item.attachments) && item.attachments.length > 0) {
    return item.attachments.map((doc) => ({
      id: doc.id || crypto.randomUUID(),
      name: doc.name || "Document",
      url: doc.url,
      addedAt: doc.addedAt || new Date().toISOString(),
      addedBy: doc.addedBy || null,
    }));
  }

  const migrated = [];
  LEGACY_DOCUMENT_FIELDS.forEach(({ key, name }) => {
    const value = item[key];
    if (!value) return;
    const urls = Array.isArray(value) ? value.filter(Boolean) : [value];
    urls.forEach((url, index) => {
      migrated.push({
        id: crypto.randomUUID(),
        name: urls.length > 1 ? `${name} ${index + 1}` : name,
        url,
        addedAt: item?.updatedAt || item?.createdAt || new Date().toISOString(),
        addedBy: null,
      });
    });
  });
  return migrated;
};

const INITIAL_STATE = {
  clientId: null,
  name: null,
  address: null,
  dob: null,
  gender: null,
  email: null,
  number: null,
  passportNumber: null,
  passportExpiry: "",
  dateOfIssue: "",
  visaStatus: "",
  visaExpiry: "",
  caseOfficer: "",
  reference: "",
  visa: "",
  visaCharge: "",
  serviceCharge: "",
  extraCharges: "",
  kotharCost: "",
  discount: "",
  finalPaymentDueDate: "",
  initialPaymentAmount: "",
  initialPaymentDueDate: "",
  visaChargeGst: defaultGstSetting(false),
  serviceChargeGst: defaultGstSetting(true),
  extraChargesGst: defaultGstSetting(false),
  kotharCostGst: defaultGstSetting(false),
  status: visaWorkStatus[0]?.label ?? "",
  documents: [],
  importantNote: "",
  tenYearAddress: [],
  type: null,
};

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

const formatClientDate = (value) => {
  if (!value) return null;
  return formatDisplayDate(value, null);
};

const getExpiryMeta = (value) => {
  const display = formatClientDate(value);
  if (!display) {
    return { display: "Not provided", tone: "muted", chip: null };
  }

  const parsed = parseToDate(value);
  const days = parsed ? differenceInDays(parsed, new Date()) : null;

  if (days == null) {
    return { display, tone: "default", chip: null };
  }
  if (days < 0) {
    return {
      display,
      tone: "error",
      chip: { label: "Expired", color: "error" },
    };
  }
  if (days <= 90) {
    return {
      display,
      tone: "warning",
      chip: { label: `Expires in ${days} day${days === 1 ? "" : "s"}`, color: "warning" },
    };
  }
  return {
    display,
    tone: "success",
    chip: null,
  };
};

const CompactDetail = ({ label, value, chip }) => (
  <Box className="min-w-0">
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontSize: 10, letterSpacing: 0.4, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ fontSize: 13 }}
        className="text-app-text break-words"
      >
        {value || "—"}
      </Typography>
      {chip && (
        <Chip
          size="small"
          label={chip.label}
          color={chip.color}
          variant="outlined"
          sx={{ height: 20, fontSize: 11 }}
        />
      )}
    </Stack>
  </Box>
);

const ClientSummaryCard = ({ data, clientList, onViewClient }) => {
  const clientId = data?.clientId ?? data?.id;
  if (!clientId) return null;

  const storedClient = clientList?.find((client) => client.id === clientId) || {};
  const profile = {
    ...storedClient,
    ...data,
    clientId,
    visaStatus:
      blankIfPlaceholder(data?.visaStatus) || blankIfPlaceholder(storedClient?.visaStatus),
    caseOfficer: data?.caseOfficer || storedClient?.caseOfficer,
  };

  const visaExpiry = getExpiryMeta(profile.visaExpiry);
  const passportExpiry = getExpiryMeta(profile.passportExpiry);

  return (
    <Paper variant="outlined" className="border-app-border">
      <Box className="px-3 py-2">
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center" className="min-w-0">
            <Avatar
              sx={{ bgcolor: "#ea580c", width: 32, height: 32, fontSize: 13, fontWeight: 700 }}
            >
              {getInitials(profile.name)}
            </Avatar>
            <Box className="min-w-0">
              <Typography variant="body2" fontWeight={700} className="truncate">
                {profile.name || "—"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                className="block truncate"
                sx={{ fontSize: 11 }}
              >
                {[profile.email, profile.number].filter(Boolean).join(" · ") || "—"}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="text"
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
            onClick={onViewClient}
            sx={{ flexShrink: 0, minWidth: "auto", px: 0.75, py: 0.25, fontSize: 12 }}
          >
            View
          </Button>
        </Stack>

        <Box className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1.5 mt-2 pt-2 border-t border-app-border">
          <CompactDetail label="Current visa" value={profile.visaStatus || "Not provided"} />
          <CompactDetail label="Visa expiry" value={visaExpiry.display} chip={visaExpiry.chip} />
          <CompactDetail label="Passport Number" value={profile.passportNumber || "Not provided"} />
          <CompactDetail
            label="Passport expiry"
            value={passportExpiry.display}
            chip={passportExpiry.chip}
          />
          <CompactDetail label="Case officer" value={profile.caseOfficer || "Not assigned"} />
          <CompactDetail
            label="Reference"
            value={profile.reference || profile.referral || "Not provided"}
          />
        </Box>
      </Box>
    </Paper>
  );
};

const SaveFirstNotice = () => (
  <Paper variant="outlined" sx={{ p: 3 }}>
    <Typography variant="body2" color="text.secondary" className="text-center">
      Save the visa application first to start tracking status updates and payments.
    </Typography>
  </Paper>
);

const TAB_QUERY_PARAM_TO_INDEX = {
  details: TAB_DETAILS,
  documents: TAB_DOCUMENTS,
  status: TAB_STATUS,
  payments: TAB_PAYMENTS,
  tasks: TAB_TASKS,
};

const AddVisaDetails = () => {
  const [data, setData] = useState(INITIAL_STATE);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    () => TAB_QUERY_PARAM_TO_INDEX[searchParams.get("tab")] ?? TAB_DETAILS
  );
  const [notifyOpen, setNotifyOpen] = useState(false);

  const [{ usersList, clientList }, { refetchClient, refetchUsers }] = useKothar();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id: routeId } = useParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!clientList?.length) refetchClient();
  }, [clientList?.length, refetchClient]);

  useEffect(() => {
    if (!usersList?.length) refetchUsers();
  }, [usersList?.length, refetchUsers]);

  usePreselectClientFromLocation({ setData, editId: routeId || state?.item?.id });

  const { data: statusHistory = [] } = useVisaStatusHistory(data?.id, Boolean(data?.id));

  const hydratedFromServerRef = useRef(false);

  const { data: fetchedVisa } = useQuery(
    ["visaDetail", routeId],
    async () => {
      const res = await axios.get(`${API_URL}/visa-applications/${routeId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      return res?.data?.data ?? null;
    },
    { enabled: !!routeId, staleTime: 0, cacheTime: 0, refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if (!state?.item || hydratedFromServerRef.current) return;
    const item = state.item;
    setData({
      ...INITIAL_STATE,
      ...item,
      documents: normalizeVisaDocuments(item),
    });
  }, [state]);

  useEffect(() => {
    if (!fetchedVisa || hydratedFromServerRef.current) return;
    hydratedFromServerRef.current = true;
    setData({
      ...INITIAL_STATE,
      ...fetchedVisa,
      documents: normalizeVisaDocuments(fetchedVisa),
    });
  }, [fetchedVisa]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const clientId = data?.clientId ?? data?.id;
  // Prefer form id; fall back to route param so tabs unlock as soon as we open an existing record.
  const recordId = data?.id || routeId;
  // Server payload first (form draft hydrates once); the tab badge shows only when > 0.
  const openVisaTaskCount = Number(fetchedVisa?.openTaskCount ?? data?.openTaskCount ?? 0);

  const relatedApplicationState = useMemo(() => {
    if (!clientId) return null;
    const storedClient = clientList?.find((client) => client.id === clientId) || {};
    return { ...storedClient, ...data, clientId };
  }, [clientId, clientList, data]);

  const openSkillAssessment = () => {
    if (!relatedApplicationState) {
      toast.error("Please select a client first");
      return;
    }
    navigate("/admin/skill-assessment/add", { state: { item: relatedApplicationState } });
  };

  const openInsurance = () => {
    if (!relatedApplicationState) {
      toast.error("Please select a client first");
      return;
    }
    navigate("/admin/insurance/add", { state: { item: relatedApplicationState } });
  };

  const exitAfterSaveRef = useRef(false);

  const { mutate, isLoading: saving } = useMutation(postData, {
    onSuccess(saved) {
      toast.success(data?.id ? "Visa application updated" : "Visa application created");
      if (saved?.id && !data?.id) {
        // First save of a new application — adopt the server id so Documents/Status/Payments
        // tabs unlock in place instead of forcing the user to reopen the record.
        setData((prev) => ({ ...prev, id: saved.id }));
        promptReferralShare({
          module: "visa",
          entryId: saved.id,
          clientId: saved?.clientId ?? clientId,
        });
      }
      queryClient.invalidateQueries(["visa-clients-page"]);
      const savedClientId = saved?.clientId ?? clientId;
      if (savedClientId != null) {
        queryClient.invalidateQueries(["visa-client-records", String(savedClientId)]);
      }
      if (exitAfterSaveRef.current) {
        navigate("/admin/visa");
      }
    },
    onError() {
      toast.error(data?.id ? "Error updating visa application" : "Error saving visa application");
    },
  });

  async function postData(payload) {
    if (data?.id) {
      const res = await axios.put(`${API_URL}/visa-applications/${payload?.id}`, payload);
      return res?.data?.data ?? null;
    }
    const res = await axios.post(`${API_URL}/visa-applications`, payload);
    return res?.data?.data ?? null;
  }

  const handleSubmit = (e, exitAfter = false) => {
    e.preventDefault();
    if (!clientId) {
      toast.error("Please select a client");
      setActiveTab(TAB_DETAILS);
      return;
    }
    exitAfterSaveRef.current = exitAfter;
    mutate({
      ...data,
      clientId,
      type: data?.type,
      tenYearAddress: data?.tenYearAddress || [],
    });
  };

  const notifyContext = useMemo(
    () => ({
      clientId,
      caseOfficer: data?.caseOfficer,
      module: "visa",
    }),
    [clientId, data?.caseOfficer]
  );

  const defaultNotifyMessage = useMemo(() => {
    const clientName = data?.name || "Client";
    const parts = [
      `Visa update for ${clientName}.`,
      data?.visa ? `Applied visa: ${data.visa}.` : null,
    ].filter(Boolean);
    return parts.join(" ");
  }, [data?.name, data?.visa]);

  const currentStatus = statusHistory?.[0]?.toStatus || data?.status;

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <SendNotificationModal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        users={usersList || []}
        context={{ ...notifyContext, defaultMessage: defaultNotifyMessage }}
      />

      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t px-4 py-4 border-b border-app-border">
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <IconButton size="small" onClick={() => navigate(-1)} aria-label="Go back">
                  <IoArrowBack className="text-xl" />
                </IconButton>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" fontWeight={700}>
                      {data?.id ? "Edit visa application" : "Add visa application"}
                    </Typography>
                    {data?.migrated && (
                      <Tooltip title="Migrated from legacy data — complete the missing details">
                        <Chip
                          label="Migrated"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: 9, height: 18 }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Manage visa details, documents, status history, and payments
                  </Typography>
                </Box>
              </Stack>
              {currentStatus && (
                <Chip label={currentStatus} color="primary" variant="outlined" size="small" />
              )}
            </Stack>
          </div>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={getThemedTabsSx()}
          >
            <Tab label="Details" />
            <ModuleLockedTab
              label="Documents"
              locked={!recordId}
              lockMessage="Create the application first to unlock this tab"
            />
            <ModuleLockedTab
              label="Status history"
              locked={!recordId}
              lockMessage="Create the application first to unlock this tab"
            />
            <ModuleLockedTab
              label="Payments"
              locked={!recordId}
              lockMessage="Create the application first to unlock this tab"
            />
            <ModuleLockedTab
              label={openVisaTaskCount > 0 ? `Tasks (${openVisaTaskCount})` : "Tasks"}
              locked={!recordId}
              lockMessage="Create the application first to unlock this tab"
            />
          </Tabs>

          <form onSubmit={(e) => handleSubmit(e, false)} className="px-4 py-6">
            {activeTab === TAB_DETAILS && (
              <Stack spacing={3}>
                <div className="flex flex-col items-start gap-3">
                  <ClientDropdown
                    data={data}
                    setData={setData}
                    disabled={Boolean(data?.id)}
                    hideEditButton
                  />

                  <ViewClientProfile
                    clientId={clientId}
                    clientName={data?.name}
                    variant="link"
                    displayName="View client profile"
                  />
                </div>
                <Box className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label="Current visa (application)"
                    placeholder="Current visa held by client"
                    name="visaStatus"
                    value={blankIfPlaceholder(data?.visaStatus) || ""}
                    onChange={handleInputChange}
                  />
                  <div className="">
                    <label
                      htmlFor="applied-visa"
                      className="block uppercase text-app-text-secondary text-xs font-semibold mb-2"
                    >
                      Applied visa
                    </label>
                    <Autocomplete
                      onChange={(_, value) =>
                        setData((prev) => ({ ...prev, visa: value?.name || "" }))
                      }
                      value={groupedVisaOptions.find((item) => item.name === data?.visa) || null}
                      options={groupedVisaOptions}
                      groupBy={(option) => option.group}
                      getOptionLabel={(option) => option?.label || option?.name || ""}
                      isOptionEqualToValue={(option, value) => option?.name === value?.name}
                      renderInput={(params) => (
                        <TextField {...params} label="Applied visa" required size="small" />
                      )}
                      ListboxProps={{ style: { maxHeight: 280 } }}
                    />
                  </div>
                </Box>
                <Paper variant="outlined" className="border-app-border p-4 mt-4">
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Charges
                  </Typography>
                  <Box className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                    <GstChargeField
                      label="Visa charge"
                      checkboxLabel="Include card surcharge"
                      amountName="visaCharge"
                      amountValue={data?.visaCharge}
                      onAmountChange={handleInputChange}
                      gst={data?.visaChargeGst}
                      onGstChange={(next) => setData((prev) => ({ ...prev, visaChargeGst: next }))}
                    />
                    <GstChargeField
                      label="Service charge"
                      amountName="serviceCharge"
                      amountValue={data?.serviceCharge}
                      onAmountChange={handleInputChange}
                      gst={data?.serviceChargeGst}
                      onGstChange={(next) =>
                        setData((prev) => ({ ...prev, serviceChargeGst: next }))
                      }
                    />
                    {/* <GstChargeField
                      label="Extra charges"
                      amountName="extraCharges"
                      amountValue={data?.extraCharges}
                      onAmountChange={handleInputChange}
                      gst={data?.extraChargesGst}
                      onGstChange={(next) =>
                        setData((prev) => ({ ...prev, extraChargesGst: next }))
                      }
                    /> */}
                    {/* <GstChargeField
                      label="Kothar cost (optional)"
                      amountName="kotharCost"
                      amountValue={data?.kotharCost}
                      onAmountChange={handleInputChange}
                      gst={data?.kotharCostGst}
                      onGstChange={(next) => setData((prev) => ({ ...prev, kotharCostGst: next }))}
                    /> */}
                  </Box>
                  <Box className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 pt-4 border-t border-app-border">
                    <InputField
                      label="Total (auto-calculated)"
                      placeholder="0.00"
                      name="totalPreview"
                      type="number"
                      disabled
                      value={computeTotal(
                        {
                          primaryCharge: data?.visaCharge,
                          serviceCharge: data?.serviceCharge,
                          extraCharges: data?.extraCharges,
                        },
                        {
                          primaryChargeGst: data?.visaChargeGst,
                          serviceChargeGst: data?.serviceChargeGst,
                          extraChargesGst: data?.extraChargesGst,
                        }
                      ).toFixed(2)}
                    />
                    {/* <InputField
                      label="Discount (optional)"
                      placeholder="0.00"
                      name="discount"
                      type="number"
                      value={data?.discount ?? ""}
                      onChange={handleInputChange}
                    /> */}
                    <InputField
                      label="Final payment due date"
                      name="finalPaymentDueDate"
                      type="date"
                      value={data?.finalPaymentDueDate ?? ""}
                      onChange={handleInputChange}
                      helperText="Shown as the remaining balance's due date on the client agreement. Left blank, the agreement reads 'Before lodgement'."
                    />
                    <InputField
                      label="Initial payment / deposit"
                      placeholder="0.00"
                      name="initialPaymentAmount"
                      type="number"
                      value={data?.initialPaymentAmount ?? ""}
                      onChange={handleInputChange}
                      helperText="Deposit the client agreement asks for while nothing is paid yet. A verified payment always replaces it."
                    />
                    <InputField
                      label="Initial payment due date"
                      name="initialPaymentDueDate"
                      type="date"
                      value={data?.initialPaymentDueDate ?? ""}
                      onChange={handleInputChange}
                      helperText="Shown as the deposit's due date on the client agreement until a payment is recorded."
                    />
                    {/* <InputField
                      label="Final amount (auto-calculated)"
                      placeholder="0.00"
                      name="finalAmountPreview"
                      type="number"
                      disabled
                      value={computeFinalAmount(
                        {
                          primaryCharge: data?.visaCharge,
                          serviceCharge: data?.serviceCharge,
                          extraCharges: data?.extraCharges,
                        },
                        {
                          primaryChargeGst: data?.visaChargeGst,
                          serviceChargeGst: data?.serviceChargeGst,
                          extraChargesGst: data?.extraChargesGst,
                        },
                        data?.discount
                      ).toFixed(2)}
                    /> */}
                  </Box>
                </Paper>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Important note
                  </Typography>
                  <textarea
                    rows={4}
                    name="importantNote"
                    value={data?.importantNote || ""}
                    onChange={handleInputChange}
                    className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm text-app-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Internal notes about this visa application…"
                  />
                </Box>
              </Stack>
            )}

            {activeTab === TAB_DOCUMENTS && (
              <Stack spacing={4}>
                {recordId && (
                  <SignatureDocumentsPanel
                    visaId={recordId}
                    client={{
                      name: data?.name,
                      email: data?.email,
                      address: data?.address,
                      visaType: data?.visa,
                    }}
                  />
                )}

                <EntryDocumentsPanel
                  moduleType="VISA"
                  entryId={recordId}
                  clientId={clientId}
                  documents={data.documents || []}
                  onDocumentsChange={(documents) => setData((prev) => ({ ...prev, documents }))}
                />
              </Stack>
            )}

            {activeTab === TAB_STATUS &&
              (data?.id ? <VisaStatusHistoryTab visaId={data.id} /> : <SaveFirstNotice />)}

            {activeTab === TAB_PAYMENTS &&
              (data?.id ? (
                <ModulePaymentsTab
                  moduleType="VISA"
                  moduleId={data.id}
                  clientId={data.clientId}
                  charges={{
                    primaryCharge: data.visaCharge,
                    serviceCharge: data.serviceCharge,
                    extraCharges: data.extraCharges,
                    primaryChargeGst: data.visaChargeGst,
                    serviceChargeGst: data.serviceChargeGst,
                    extraChargesGst: data.extraChargesGst,
                    discount: data.discount,
                  }}
                  onPaymentSubmitted={() => setNotifyOpen(true)}
                />
              ) : (
                <SaveFirstNotice />
              ))}

            {activeTab === TAB_TASKS &&
              (recordId ? (
                <ModuleTasksTab
                  entryType="VISA"
                  entryId={recordId}
                  clientId={clientId}
                  clientName={data?.name}
                  taskModule="Visa"
                  assignTitle={`Visa: ${data?.visa || "Visa application"} (${
                    data?.name || "client"
                  })`}
                  onTasksChanged={() =>
                    queryClient.invalidateQueries({ queryKey: ["visaDetail", routeId] })
                  }
                />
              ) : (
                <SaveFirstNotice />
              ))}

            <Divider sx={{ my: 3 }} />
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save & Exit"}
              </Button>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving ? "Saving…" : data?.id ? "Update application" : "Create application"}
              </Button>
            </Stack>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddVisaDetails;
